import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Row, Col, Button } from 'react-bootstrap';
import classNames from 'classnames';
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon';
import { FormikContextType, useFormikContext } from 'formik';
import history from 'browserHistory';

import { TripStatus } from 'entities/Trip';
import { cleanTripFields } from 'modules/common/FillTrip';
import ModalWrapper from 'modules/shared/ModalWrapper';
import { cleanTrip, deleteTripRequest, updateTripRequest, saveTrip } from 'modules/common/CommonTrip/actions';
import { tripSavedSelector } from 'modules/common/CommonTrip/selectors';
import { deleteTab } from 'modules/common/Tabs/actions';
import { tripIntentionStatuses, tripStatuses } from '../../../entities/Trip/constants';
import useModal from '../hooks/useModal';
import YesNoModal from '../YesNoModal';
import TopBorderBtn from '../TopBorderBtn';

import styles from './FlightTop.module.scss';

type Props = {
  tripId?: number;
  handleChange: (e: React.ChangeEvent<any>) => void;
  tripIntention?: number;
  status?: TripStatus;
  isSecurity?: boolean;
  hideTripIntention?: boolean;
};

const FlightTop: React.FC<Props> = ({
  tripId,
  handleChange,
  tripIntention,
  status = {},
  isSecurity,
  hideTripIntention,
}) => {
  const formik = useFormikContext();
  const { values, touched, isValid }: FormikContextType<any> = formik;

  const { isShow, closeModal, openModal } = useModal();
  const { isShow: isShowSave, openModal: openModalSave, closeModal: closeModalSave } = useModal();
  const { isShow: isShowCloseTab, openModal: openCloseTab, closeModal: closeCloseTab } = useModal();

  const { saved, forceSave } = useSelector(tripSavedSelector);

  const dispatch = useDispatch();
  const { id: statusId = 1, title = '' } = status;
  const isNotCreateStatus = statusId !== tripStatuses.create;

  const deleteTripHandler = () => {
    closeModal();
    dispatch(deleteTripRequest(tripId || 0, values.tripIntention || 1));
    dispatch(cleanTripFields());
  };

  const path = history.location.pathname.split('/')[1];
  const isOrdering = path === 'ordering';
  const isDisableSaveBtn = isOrdering && !isValid && statusId > 2;

  const saveHandler = () => {
    if (isShowCloseTab) closeCloseTab();
    if (isShow) closeModal();

    dispatch(updateTripRequest({ ...values, id: tripId, statusId }, true));
    dispatch(cleanTripFields());

    openModalSave();
    setTimeout(() => closeModalSave(), 3000);
  };

  const closeHandler = () => {
    // @ts-ignore
    dispatch(deleteTab(`trip_${tripId}`));
    // eslint-disable-next-line
    const url = `${forceSave ? forceSave : '/' + path}`;
    if (values.tripIntention === 2) {
      history.push(`${url}/unload`);
    } else {
      history.push(url);
    }

    dispatch(cleanTrip());
    dispatch(cleanTripFields());
    closeCloseTab();
  };

  const isSaveForSecurity =
    !Object.keys(touched).length &&
    (statusId === tripStatuses.create || statusId === tripStatuses.parkingInLine || isOrdering);

  window.onpopstate = (e: any) => {
    e.preventDefault();
    closeHandler();
  };

  useEffect(() => {
    if (forceSave) {
      dispatch(updateTripRequest({ ...values, id: tripId, statusId }, true));
      closeHandler();
    }

    return () => window.removeEventListener('popstate', closeHandler);
  }, [forceSave]);

  useEffect(() => {
    if (Object.keys(touched).length) {
      dispatch(saveTrip(false));
    }
  }, [Object.keys(touched).length]);

  const tripIntentionStr = values.tripIntention === 1 ? 'на завантаження' : 'на розвантаження';
  const tstr = !hideTripIntention ? '' : tripIntentionStr;
  return (
    <>
      <p className="text-center font-weight-bolder mb-1">{`Рейс ${tstr} № ${
        tripId || ''
      } (${title}, статус ${statusId})`}</p>
      <fieldset className="pl-4 mb-2">
        <Form.Group as={Row} className="position-relative">
          <Col className="d-flex align-items-center">
            {!tripIntention && <AlertCircleOutlineIcon className={classNames('text-danger', styles.icon)} />}
            {!hideTripIntention && (
              <Form.Check
                className={classNames('flex-row-reverse font-weight-bolder', styles.radio)}
                type="radio"
                label="Завантаження"
                name="tripIntention"
                id="tripIntention1"
                inline
                value={tripIntentionStatuses.loading}
                checked={String(tripIntention) === String(tripIntentionStatuses.loading)}
                onChange={handleChange}
                disabled={isNotCreateStatus}
              />
            )}
            {!hideTripIntention && (
              <Form.Check
                className={classNames('flex-row-reverse font-weight-bolder', styles.radio)}
                type="radio"
                label="Розвантаження"
                name="tripIntention"
                id="tripIntention2"
                inline
                value={tripIntentionStatuses.unloading}
                checked={String(tripIntention) === String(tripIntentionStatuses.unloading)}
                onChange={handleChange}
                disabled={isNotCreateStatus}
              />
            )}
          </Col>
          <Col className="d-flex justify-content-end">
            {statusId === tripStatuses.create && (
              <TopBorderBtn icon="delete" clickHandler={openModal} tooltip="Видалити рейс" />
            )}
            {(statusId === tripStatuses.create ||
              statusId === tripStatuses.parkingInLine ||
              statusId === tripStatuses.unloading ||
              (isOrdering && statusId !== tripStatuses.waitingAdmin) ||
              isSecurity) && (
              <TopBorderBtn
                icon="save"
                clickHandler={saveHandler}
                tooltip="Зберегти данні"
                disabled={isDisableSaveBtn}
              />
            )}
            <TopBorderBtn
              icon="close"
              // eslint-disable-next-line
              clickHandler={saved ? closeHandler : isSaveForSecurity ? closeHandler : openCloseTab}
              tooltip="Закрити вкладку"
            />
          </Col>
        </Form.Group>
      </fieldset>

      <ModalWrapper show={isShowSave} onHide={closeModalSave}>
        <div>
          <h6 className="text-center">Зміни збережено</h6>
          <div className="d-flex justify-content-center mt-3">
            <Button className="px-5" variant="outline-secondary" onClick={closeModalSave}>
              Ok
            </Button>
          </div>
        </div>
      </ModalWrapper>

      <YesNoModal isShow={isShow} title="Видалити рейс" closeModal={closeModal} acceptHandler={deleteTripHandler} />

      {!saved && status && status.id === 1 ? (
        <YesNoModal
          isShow={isShowCloseTab}
          title="Бажаєте зберегти зміни"
          acceptHandler={() => {
            saveHandler();
            setTimeout(() => closeHandler(), 1500);
          }}
          closeHandler={closeCloseTab}
          closeModal={closeHandler}
        />
      ) : (
        <YesNoModal
          isShow={isShowCloseTab}
          title="Закрити вкладку"
          acceptHandler={closeHandler}
          closeModal={() => closeCloseTab()}
        />
      )}
    </>
  );
};

export default FlightTop;
